import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import SplitPane, { Pane } from 'split-pane-react'

import { Sash } from './NavigationSplitPane/styles'

type ResizableSidebarLayoutProps = PropsWithChildren<{
	content: React.ReactNode
	defaultWidth?: number
	localStorageKey: string
	maxWidth?: number
	minWidth?: number
}>

const readStoredWidth = (key: string, fallback: number) => {
	try {
		const raw = localStorage.getItem(key)
		if (!raw) return fallback
		const parsed = Number(raw)
		return Number.isFinite(parsed) ? parsed : fallback
	} catch {
		return fallback
	}
}

export const ResizableSidebarLayout = ({
	children,
	content,
	defaultWidth = 280,
	localStorageKey,
	maxWidth = 520,
	minWidth = 220,
}: ResizableSidebarLayoutProps) => {
	const [sizes, setSizes] = useState<(number | string)[]>([
		defaultWidth,
		'auto',
	])

	useEffect(() => {
		setSizes([readStoredWidth(localStorageKey, defaultWidth), 'auto'])
	}, [defaultWidth, localStorageKey])

	const sidebarWidth = useMemo(() => {
		const width = sizes[0]
		return typeof width === 'number' ? width : defaultWidth
	}, [defaultWidth, sizes])

	useEffect(() => {
		try {
			localStorage.setItem(localStorageKey, String(sidebarWidth))
		} catch {
			// ignore private mode / quota
		}
	}, [localStorageKey, sidebarWidth])

	return (
		<SplitPane
			split="vertical"
			sizes={sizes}
			onChange={setSizes}
			sashRender={() => <Sash />}
			allowResize
		>
			<Pane minSize={minWidth} maxSize={maxWidth}>
				{children}
			</Pane>
			<Pane minSize={340}>{content}</Pane>
		</SplitPane>
	)
}
